import React, { useState, useEffect, useCallback } from 'react';
import { H5 } from '../../../../AbstractElements';
import { FormGroup, Row, Col, Input, Button, Card, Label, Spinner } from 'reactstrap';
import { PlusCircle } from 'react-feather';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { fetchFormData, uploadFile, deleteFile } from '../../../../Api/ApiMethods';
import { normalizeUppercaseTR } from '../../../../_helper/regexHandler';
import FileCard from './FileCard';

// FilesComponent - Gelişmiş Dosya Yönetimi Bileşeni
const FilesComponent = ({
    onFilesChange,
    existingFiles = [],
    multiple = true,
    canEdit = true,
    callFormReference = false, // true: çağrı formu için, false: diğer objeler için
    referenceId, // callFormReference=true ise callFormId, false ise objeId
    typeFilter = null, // Belirli dosya türlerini title'a göre filtrelemek için array (örn: ['VEKALETNAME', 'FATURA'])
    excludeTypes = false, // true ise typeFilter'daki türleri hariç tut, false ise sadece o türleri göster
    title = "Belgeler" // Başlık özelleştirilebilir
}) => {
    console.log('[DEBUG] FilesComponent mounted/re-rendered with props:', { referenceId, typeFilter, excludeTypes, title });
    
    // State tanımlamaları
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [fileTypes, setFileTypes] = useState([]);
    const [currentFile, setCurrentFile] = useState(null);
    const [currentFileTypeId, setCurrentFileTypeId] = useState('');
    const [isAddingFile, setIsAddingFile] = useState(false);
    const [loadingTypes, setLoadingTypes] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [deletingFileId, setDeletingFileId] = useState(null);
    const [isFetchingFiles, setIsFetchingFiles] = useState(false);

    const extractItems = (response) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.items)) return response.items;
        if (Array.isArray(response?.data?.items)) return response.data.items;
        if (Array.isArray(response?.data)) return response.data;
        return [];
    };

    const normalizeTypeTitle = (title) =>
        normalizeUppercaseTR((title || '').toString().trim());

    // Dosya türlerini API'den çek
    const fetchFileTypes = useCallback(async () => {
        console.log('[DEBUG] FilesComponent fetchFileTypes called');
        setLoadingTypes(true);
        try {
            console.log('[DEBUG] FilesComponent fetching file types from /filetype');
            const data = await fetchFormData({
                apiEndpoint: '/filetype',
                page: 1,
                pageSize: 1000, // Tüm türleri çek
                search: ''
            });

            console.log('[DEBUG] FilesComponent file types API response:', data);
            const responseItems = extractItems(data);
            if (Array.isArray(responseItems)) {
                let types = responseItems;

                // typeFilter varsa title'a göre filtrele
                if (typeFilter && Array.isArray(typeFilter) && typeFilter.length > 0) {
                    const normalizedFilter = typeFilter.map(normalizeTypeTitle);
                    if (excludeTypes) {
                        // excludeTypes true ise, typeFilter'daki türleri hariç tut
                        types = types.filter(type => !normalizedFilter.includes(normalizeTypeTitle(type.title || type.name)));
                    } else {
                        // excludeTypes false ise, sadece typeFilter'daki türleri göster
                        types = types.filter(type => normalizedFilter.includes(normalizeTypeTitle(type.title || type.name)));
                    }
                }

                setFileTypes(types);
            } else {
                setFileTypes([]);
                console.warn('FileTypes API response does not contain items array');
            }
        } catch (error) {
            console.error('Error fetching file types:', error);
            toast.error('Dosya türleri yüklenirken hata oluştu.');
            setFileTypes([]);
        } finally {
            setLoadingTypes(false);
        }
    }, [JSON.stringify(typeFilter), excludeTypes]);    // typeFilter'ı JSON.stringify ile stabilize et, excludeTypes ekle

    // Track component mounting
    useEffect(() => {
        console.log('[DEBUG] FilesComponent mounted');
        return () => {
            console.log('[DEBUG] FilesComponent unmounted');
        };
    }, []);

    // Component mount edildiğinde dosya türlerini çek
    useEffect(() => {
        fetchFileTypes();
    }, [fetchFileTypes]);

    // referenceId değiştiğinde mevcut dosyaları çek
    useEffect(() => {
        const fetchFiles = async () => {
            if (!referenceId) {
                setUploadedFiles([]); // referenceId yoksa dosyaları temizle
                setIsFetchingFiles(false);
                return;
            }

            // Eğer typeFilter var ve excludeTypes=false ise (server-side filtreleme yapacaksak)
            // fileTypes'ın yüklenmesini bekle
            if (typeFilter && Array.isArray(typeFilter) && typeFilter.length > 0 && !excludeTypes && fileTypes.length === 0) {
                console.log('[DEBUG] FilesComponent waiting for fileTypes to load for server-side filtering');
                return; // fileTypes henüz yüklenmemiş, bekle
            }

            setIsFetchingFiles(true);
            try {
                const params = {
                    page: 1,
                    pageSize: 50,
                    search: ''
                };

                // Çağrı formu veya diğer objeler için farklı parametre ekleme
                if (callFormReference) {
                    params.callFormReference = referenceId;
                } else {
                    params.reference = referenceId;
                }

                // typeFilter varsa ve fileTypes yüklenmişse, fileType ID'lerini parametre olarak ekle
                // Ancak sadece excludeTypes=false ise (yani sadece belirli türleri göstermek istiyorsak)
                if (typeFilter && Array.isArray(typeFilter) && typeFilter.length > 0 && fileTypes.length > 0 && !excludeTypes) {
                    const filteredTypeIds = fileTypes
                        .filter(type => typeFilter.includes(type.title))
                        .map(type => type.id);
                    
                    if (filteredTypeIds.length > 0) {
                        // Tek fileType için filtreleme (sadece excludeTypes=false iken)
                        if (filteredTypeIds.length === 1) {
                            params.fileType = filteredTypeIds[0];
                        }
                    }
                }            console.log('[DEBUG] FilesComponent making API call to /UploadedFile with params:', params);
            const data = await fetchFormData({
                apiEndpoint: '/UploadedFile',
                ...params
            });

                const uploadedFileItems = extractItems(data);
                if (Array.isArray(uploadedFileItems)) {
                    console.log('FilesComponent DEBUG - API Response:', {
                        callFormReference,
                        referenceId,
                        typeFilter,
                        excludeTypes,
                        totalItems: uploadedFileItems.length,
                        items: uploadedFileItems.map(item => ({
                            fileName: item.fileName,
                            typeTitle: item.type?.title
                        }))
                    });

                    let files = uploadedFileItems.map((item, index) => {
                        // Type bilgisini çeşitli kaynaklardan al
                        let typeInfo = null;
                        let typeTitle = null;

                        if (item.type) {
                            typeInfo = item.type;
                            typeTitle = typeof item.type === 'object' ? item.type.title : null;
                        } else if (item.fileType) {
                            typeInfo = item.fileType.id || item.fileType;
                            typeTitle = item.fileType.title;
                        } else if (item.typeId) {
                            typeInfo = item.typeId;
                        }

                        return {
                            id: item.id || Date.now() + index,
                            file: item, // Sunucudan gelen dosya bilgisi
                            type: typeInfo, // API'den gelen type objesi veya ID
                            typeTitle: typeTitle, // Type'ın title'ı
                            fileName: item.fileName || item.name || 'Bilinmeyen Dosya',
                            isExisting: true,
                            serverResponse: item
                        };
                    });

                    // Client-side filtreleme: typeFilter varsa, excludeTypes'a göre filtrele
                    if (typeFilter && Array.isArray(typeFilter) && typeFilter.length > 0) {
                        const normalizedFilter = typeFilter.map(normalizeTypeTitle);
                        const originalLength = files.length;
                        files = files.filter(file => {
                            const fileTypeTitle = normalizeTypeTitle(file.typeTitle);
                            const matches = fileTypeTitle && normalizedFilter.includes(fileTypeTitle);
                            
                            // excludeTypes true ise, typeFilter'daki türleri hariç tut (tersini döndür)
                            // excludeTypes false ise, sadece typeFilter'daki türleri göster
                            return excludeTypes ? !matches : matches;
                        });
                        
                        // Debug log
                        if (originalLength !== files.length) {
                            console.log(`FilesComponent filtered files for typeFilter [${typeFilter.join(', ')}] excludeTypes=${excludeTypes}: ${originalLength} -> ${files.length}`);
                        }
                    }

                    setUploadedFiles(multiple ? files : files.slice(0, 1));
                } else {
                    setUploadedFiles([]);
                }
            } catch (error) {
                console.error('Error fetching existing files:', error);
                // Hata durumunda sessizce devam et, kullanıcıya error gösterme
                setUploadedFiles([]);
            } finally {
                setIsFetchingFiles(false);
            }
        };

        fetchFiles();
    }, [referenceId, callFormReference, multiple, fileTypes]); // fileTypes dependency eklendi

    // existingFiles prop'u varsa manuel olarak eklenen dosyalarla birleştir (opsiyonel backward compatibility)
    useEffect(() => {
        if (existingFiles && existingFiles.length > 0 && !referenceId) {
            // Sadece referenceId yoksa existingFiles'ı kullan (fallback)
            const initialFiles = existingFiles.map((f, index) => ({
                id: f.id || Date.now() + index,
                file: f.file || f,
                type: f.type || f.typeId,
                fileName: f.fileName || f.name || 'Bilinmeyen Dosya',
                isExisting: true
            })).filter(f => f.file);

            setUploadedFiles(multiple ? initialFiles : initialFiles.slice(0, 1));
        }
    }, [existingFiles, multiple, referenceId]);

    // Parent component'e dosya değişikliklerini bildir
    useEffect(() => {
        onFilesChange(uploadedFiles);
    }, [uploadedFiles]); // onFilesChange'i dependency'den çıkar    // Dosya türü adını ID'den bul veya type objesinden al
    const getTypeNameById = (typeIdOrObject, uploadedFile = null) => {
        // Önce uploadedFile'da tanımlı typeTitle'ı kontrol et
        if (uploadedFile && uploadedFile.typeTitle) {
            return uploadedFile.typeTitle;
        }

        // Eğer type bir obje ise (API'den gelen dosyalar için)
        if (typeof typeIdOrObject === 'object' && typeIdOrObject?.title) {
            return typeIdOrObject.title;
        }

        // Eğer sadece ID ise, fileTypes listesinden bul
        const type = fileTypes.find(ft => ft.id === typeIdOrObject);
        return type ? (type.title || type.name || 'Bilinmeyen Tür') : 'Bilinmeyen Tür';
    };// Dosya ekleme işlemini başlat
    const handleAddFileClick = () => {
        if (!referenceId) {
            if (callFormReference) {
                toast.warning('Dosya yüklemek için önce çağrı formunu kaydetmeniz gerekiyor.');
            } else {
                toast.warning('Dosya yüklemek için önce kaydı kaydetmeniz gerekiyor.');
            }
            return;
        }
        setIsAddingFile(true);
        setCurrentFile(null);
        setCurrentFileTypeId('');
    };

    // Dosya seçimi
    const handleFileSelect = (event) => {
        if (event.target.files && event.target.files[0]) {
            setCurrentFile(event.target.files[0]);
        } else {
            setCurrentFile(null);
        }
    };

    // Dosya türü seçimi
    const handleTypeSelect = (event) => {
        setCurrentFileTypeId(event.target.value);
    };

    // Dosya yükleme işlemi
    const handleConfirmAdd = async () => {
        if (!currentFile) {
            toast.warning('Lütfen bir dosya seçin.');
            return;
        }

        if (!currentFileTypeId) {
            toast.warning('Lütfen dosya türünü seçin.');
            return;
        }
        
        if (!referenceId) {
            if (callFormReference) {
                toast.warning('Referans ID bulunamadı. Önce çağrı formunu kaydetmeniz gerekiyor.');
            } else {
                toast.warning('Referans ID bulunamadı. Önce kaydı kaydetmeniz gerekiyor.');
            }
            return;
        }

        // Aynı isimde dosya kontrolü (sadece multiple=true için)
        if (multiple && uploadedFiles.some(f => f.fileName === currentFile.name)) {
            toast.warning(`"${currentFile.name}" adlı dosya zaten eklenmiş.`);
            return;
        }

        setUploadingFile(true);

        try {
            // UploadFile API'sine dosya yükle
            const uploadData = {
                apiEndpoint: '/UploadedFile',
                fileName: currentFile.name,
                fileTypeId: currentFileTypeId,
                file: currentFile,
                reference: callFormReference ? null : referenceId, // callForm değilse reference'a yaz
                callFormReference: callFormReference ? referenceId : null // callForm ise callFormReference'a yaz
            };

            const response = await uploadFile(uploadData);

            if (response && response.isSuccess) {
                // Başarılı yükleme - sunucu cevabını mevcut dosya mapping'i ile normalize et
                const item = response.data || {};

                let typeInfo = null;
                let typeTitle = null;
                if (item.type) {
                    typeInfo = item.type;
                    typeTitle = typeof item.type === 'object' ? item.type.title : null;
                } else if (item.fileType) {
                    typeInfo = item.fileType.id || item.fileType;
                    typeTitle = item.fileType.title;
                } else if (item.typeId) {
                    typeInfo = item.typeId;
                }

                // Sunucudan type objesi gelmediyse, fileTypes listesinden title'ı ekle
                if (!typeTitle) {
                    const matched = fileTypes.find(ft => ft.id === currentFileTypeId);
                    if (matched) typeTitle = matched.title || matched.name;
                }

                const newFileData = {
                    id: item.id || Date.now(),
                    file: item,
                    type: typeInfo || currentFileTypeId,
                    typeTitle: typeTitle,
                    fileName: item.fileName || currentFile.name,
                    isExisting: true,
                    serverResponse: item
                };

                setUploadedFiles(prev => multiple ? [...prev, newFileData] : [newFileData]);

                toast.success('Dosya başarıyla yüklendi.');

                // Form'u temizle
                setIsAddingFile(false);
                setCurrentFile(null);
                setCurrentFileTypeId('');
            } else {
                toast.error('Dosya yüklenemedi.');
            }
        } catch (error) {
            console.error('File upload error:', error);
            toast.error('Dosya yüklenirken hata oluştu.');
        } finally {
            setUploadingFile(false);
        }
    };

    // Dosya ekleme işlemini iptal et
    const handleCancelAdd = () => {
        setIsAddingFile(false);
        setCurrentFile(null);
        setCurrentFileTypeId('');
    };    // Dosya silme işlemi
    const handleRemoveFile = async (fileIdToRemove, fileData) => {
        // Onay diyalogu
        const isExisting = fileData.isExisting;
        const confirmMessage = isExisting
            ? `"${getFileName(fileData)}" dosyasını sunucudan kalıcı olarak silmek istediğinizden emin misiniz?`
            : `"${getFileName(fileData)}" dosyasını listeden çıkarmak istediğinizden emin misiniz?`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        setDeletingFileId(fileIdToRemove);

        try {
            // Eğer sunucuda bulunan bir dosya ise API'den sil
            if (isExisting && fileData.serverResponse && fileData.serverResponse.id) {
                await deleteFile(fileData.serverResponse.id);
                toast.success('Dosya başarıyla silindi.');
            } else {
                toast.success('Dosya listeden çıkarıldı.');
            }

            // Her durumda listeden çıkar
            setUploadedFiles(prevFiles => prevFiles.filter(f => f.id !== fileIdToRemove));
        } catch (error) {
            console.error('Error removing file:', error);
            // Hata durumunda kullanıcıya bildirme, API silme başarısız oldu
            toast.error('Dosya silinemedi. Lütfen tekrar deneyin.');
        } finally {
            setDeletingFileId(null);
        }
    };

    // Dosya adını al
    const getFileName = (fileData) => {
        if (fileData.fileName) return fileData.fileName;
        if (fileData.file instanceof File) return fileData.file.name;
        if (typeof fileData.file === 'object' && fileData.file?.name) return fileData.file.name;
        return 'Bilinmeyen Dosya';
    };    // Dosya indirme (mevcut dosyalar için)
    const handleDownloadFile = (fileData) => {
        if (fileData.isExisting && fileData.serverResponse) {
            // Dosyanın file array'indeki ilk ID'yi al
            const fileId = fileData.serverResponse.file && fileData.serverResponse.file.length > 0
                ? fileData.serverResponse.file[0]
                : fileData.serverResponse.id;

            // FILE_API_PREVIEW_ENDPOINT kullanarak indirme URL'i oluştur
            const downloadUrl = `${process.env.REACT_APP_FILE_API_PREVIEW_ENDPOINT}/${fileId}`;

            // Yeni sekmede aç
            window.open(downloadUrl, '_blank');
        } else {
            toast.warning('Bu dosya henüz sunucuya yüklenmemiş.');
        }
    };

    // Layout hesaplamaları
    const fileInputColSize = 5;
    const typeSelectColSize = 4;
    const buttonColSize = 3;

    return (
        <>
            <H5>{title}</H5>

            {/* Dosya ekleme butonu */}
            {canEdit && !isAddingFile && (multiple || uploadedFiles.length === 0) && (
                <Button
                    color="primary"
                    outline
                    onClick={handleAddFileClick}
                    className="my-3 d-flex align-items-center gap-2"
                    disabled={!referenceId}
                >
                    <PlusCircle size={18} />
                    <span className='mb-0'>Dosya Ekle</span>
                </Button>
            )}            {!referenceId && (
                <p className="text-warning mb-3">
                    <small>
                        {callFormReference
                            ? 'Dosya yüklemek için önce çağrı formunu kaydetmeniz gerekiyor.'
                            : 'Dosya yüklemek için önce kaydı kaydetmeniz gerekiyor.'
                        }
                    </small>
                </p>
            )}

            {/* Dosya ekleme formu */}
            {canEdit && isAddingFile && (
                <Card className="my-3 p-3 border">
                    <Row className="g-2 align-items-end">
                        {/* Dosya seçimi */}
                        <Col md={fileInputColSize}>
                            <FormGroup className="m-0">
                                <Label for="fileInput">Dosya Seç</Label>
                                <Input
                                    id="fileInput"
                                    type="file"
                                    onChange={handleFileSelect}
                                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                                    disabled={uploadingFile}
                                />
                            </FormGroup>
                        </Col>

                        {/* Dosya türü seçimi */}
                        <Col md={typeSelectColSize}>
                            <FormGroup className="m-0">
                                <Label for="fileTypeSelect">
                                    Dosya Türü
                                    {loadingTypes && <Spinner size="sm" className="ms-2" />}
                                </Label>
                                <Input
                                    id="fileTypeSelect"
                                    type="select"
                                    value={currentFileTypeId}
                                    onChange={handleTypeSelect}
                                    disabled={loadingTypes || uploadingFile}
                                >
                                    <option value="">Tür Seçiniz...</option>
                                    {fileTypes.length > 0 ? (
                                        fileTypes.map(type => (
                                            <option key={type.id} value={type.id}>
                                                {type.title || type.name}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="" disabled>
                                            {loadingTypes ? 'Yükleniyor...' : 'Dosya türü bulunamadı'}
                                        </option>
                                    )}
                                </Input>
                            </FormGroup>
                        </Col>

                        {/* İşlem butonları */}
                        <Col md={buttonColSize} className="d-flex justify-content-end align-items-end">
                            <Button
                                color="success"
                                size="sm"
                                outline
                                onClick={handleConfirmAdd}
                                className="me-2"
                                disabled={uploadingFile || !currentFile || !currentFileTypeId}
                            >
                                {uploadingFile ? <Spinner size="sm" /> : 'Yükle'}
                            </Button>
                            <Button
                                color="danger"
                                size="sm"
                                outline
                                onClick={handleCancelAdd}
                                disabled={uploadingFile}
                            >
                                İptal
                            </Button>
                        </Col>
                    </Row>

                    {currentFile && (
                        <small className='text-muted mt-1 d-block'>
                            Seçilen dosya: {currentFile.name} ({(currentFile.size / 1024).toFixed(2)} KB)
                        </small>
                    )}
                </Card>
            )}

            {/* Yüklenen dosyalar — kart grid görünümü */}
            {uploadedFiles.length > 0 && (
                <FormGroup>
                    {multiple ? (
                        <Row className="g-3">
                            {uploadedFiles.map((uploadedFile) => (
                                <Col key={uploadedFile.id} xs="12" md="6" lg="4" xl="3">
                                    <FileCard
                                        uploadedFile={uploadedFile}
                                        typeName={getTypeNameById(uploadedFile.type, uploadedFile)}
                                        onDownload={() => handleDownloadFile(uploadedFile)}
                                        onDelete={canEdit ? () => handleRemoveFile(uploadedFile.id, uploadedFile) : null}
                                        isDeleting={deletingFileId === uploadedFile.id}
                                    />
                                </Col>
                            ))}
                        </Row>
                    ) : (
                        uploadedFiles.map((uploadedFile) => (
                            <div key={uploadedFile.id} style={{ maxWidth: '260px' }}>
                                <FileCard
                                    uploadedFile={uploadedFile}
                                    typeName={getTypeNameById(uploadedFile.type, uploadedFile)}
                                    onDownload={() => handleDownloadFile(uploadedFile)}
                                    onDelete={canEdit ? () => handleRemoveFile(uploadedFile.id, uploadedFile) : null}
                                    isDeleting={deletingFileId === uploadedFile.id}
                                />
                            </div>
                        ))
                    )}
                </FormGroup>
            )}

            {/* Yükleniyor göstergesi */}
            {isFetchingFiles && (
                <p className="text-muted d-flex align-items-center gap-2">
                    <Spinner size="sm" /> Dosyalar yükleniyor...
                </p>
            )}

            {/* Dosya yoksa mesaj — sadece fetch bittikten sonra göster */}
            {!isFetchingFiles && uploadedFiles.length === 0 && !isAddingFile && (
                <p className="text-muted">Henüz dosya yüklenmedi.</p>
            )}
        </>
    );
};

// PropTypes tanımlamaları
FilesComponent.propTypes = {
    onFilesChange: PropTypes.func.isRequired,
    existingFiles: PropTypes.array,
    multiple: PropTypes.bool,
    canEdit: PropTypes.bool,
    callFormReference: PropTypes.bool, // true: çağrı formu, false: diğer objeler
    referenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // İlgili obje ID'si
    typeFilter: PropTypes.arrayOf(PropTypes.string), // Dosya türü filtresi (title'lara göre)
    excludeTypes: PropTypes.bool, // true ise typeFilter'daki türleri hariç tut
    title: PropTypes.string // Başlık
};

export default FilesComponent;
